import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { PLANS, type PlanConfig, getPrice, formatPlanPrice } from '@/lib/stripe';
import { PlanCheckoutModal } from './PlanCheckoutModal';
import { useGeolocatedCurrency } from '@/hooks/useGeolocatedCurrency';

export function LandingPricingSection() {
  const { t } = useTranslation();
  const [isYearly, setIsYearly] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<PlanConfig | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { currency: geoCurrency } = useGeolocatedCurrency();

  const plans = Object.values(PLANS).filter(p => p.id !== 'enterprise');

  const handleSelectPlan = (plan: PlanConfig) => {
    sessionStorage.setItem('purchase_flow', '1');
    setSelectedPlan(plan);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    sessionStorage.removeItem('purchase_flow');
    setModalOpen(false);
  };

  const handleContactSales = () => {
    window.open('mailto:sales@digitais.ai?subject=Enterprise%20Plan%20Inquiry', '_blank');
  };

  const getPriceDisplay = (plan: PlanConfig) => {
    if (plan.id === 'enterprise') return t('landing.pricing.contactSales');
    const price = getPrice(plan, isYearly ? 'yearly' : 'monthly', geoCurrency);
    return formatPlanPrice(price, geoCurrency);
  };

  const getIntervalDisplay = () => isYearly ? t('landing.pricing.perYear') : t('landing.pricing.perMonth');

  const getSavingsDisplay = (plan: PlanConfig) => {
    if (plan.id === 'enterprise' || !isYearly) return null;
    const monthlyTotal = getPrice(plan, 'monthly', geoCurrency) * 12;
    const yearlyTotal = getPrice(plan, 'yearly', geoCurrency);
    const savings = monthlyTotal - yearlyTotal;
    if (savings > 0) return t('landing.pricing.saveAmount', { amount: formatPlanPrice(savings, geoCurrency) });
    return null;
  };

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1A1F3C]">
            {t('landing.pricing.title')}
          </h2>
          <p className="text-lg text-[#1A1F3C]/60 max-w-2xl mx-auto mb-8">
            {t('landing.pricing.subtitle')}
          </p>

          <div className="flex items-center justify-center gap-4 relative">
            <Label htmlFor="billing-toggle" className={!isYearly ? 'font-semibold text-[#1A1F3C]' : 'text-[#1A1F3C]/50'}>
              {t('landing.pricing.monthly')}
            </Label>
            <Switch id="billing-toggle" checked={isYearly} onCheckedChange={setIsYearly} className="data-[state=checked]:bg-[#4F6CFF] data-[state=unchecked]:bg-[#1A1F3C]/30" />
            <Label htmlFor="billing-toggle" className={isYearly ? 'font-semibold text-[#1A1F3C]' : 'text-[#1A1F3C]/50'}>
              {t('landing.pricing.yearly')}
            </Label>
            {isYearly && (
              <Badge className="absolute left-[calc(50%+120px)] top-1/2 -translate-y-1/2 bg-[#00C896] text-white whitespace-nowrap">
                {t('landing.pricing.save2Months')}
              </Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative flex flex-col ${
                plan.popular
                  ? 'border-[#4F6CFF] border-2 shadow-lg scale-105 z-10'
                  : 'border-[#1A1F3C]/10'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#4F6CFF] text-white">
                    <Sparkles className="h-3 w-3 mr-1" />
                    {t('landing.pricing.mostPopular')}
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <CardTitle className="text-xl text-[#1A1F3C]">{t(plan.nameKey)}</CardTitle>
                <CardDescription>{t(plan.descriptionKey)}</CardDescription>
              </CardHeader>

              <CardContent className="flex-1">
                <div className="text-center mb-6">
                  <span className="text-4xl font-bold text-[#1A1F3C]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {getPriceDisplay(plan)}
                  </span>
                  {plan.id !== 'enterprise' && (
                    <span className="text-[#1A1F3C]/50 ml-1">{getIntervalDisplay()}</span>
                  )}
                  {getSavingsDisplay(plan) && (
                    <p className="text-sm text-[#00C896] mt-1">{getSavingsDisplay(plan)}</p>
                  )}
                </div>

                <ul className="space-y-3">
                  {plan.features.map((featureKey, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-[#00C896] shrink-0 mt-0.5" />
                      <span className="text-sm text-[#1A1F3C]/70">{t(featureKey)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button
                  className={`w-full ${plan.popular ? 'bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white' : ''}`}
                  variant={plan.popular ? 'default' : 'outline'}
                  onClick={() => handleSelectPlan(plan)}
                >
                  {t('landing.pricing.getStarted')}
                </Button>
              </CardFooter>
            </Card>
          ))}

          {/* Enterprise */}
          <Card className="relative flex flex-col border-[#1A1F3C]/10">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-xl text-[#1A1F3C]">{t('billing.plans.enterprise.name')}</CardTitle>
              <CardDescription>{t('billing.plans.enterprise.description')}</CardDescription>
            </CardHeader>

            <CardContent className="flex-1">
              <div className="text-center mb-6">
                <span className="text-2xl font-bold text-[#1A1F3C]">{t('landing.pricing.customPricing')}</span>
              </div>
              <ul className="space-y-3">
                {PLANS.enterprise.features.map((featureKey, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-[#00C896] shrink-0 mt-0.5" />
                    <span className="text-sm text-[#1A1F3C]/70">{t(featureKey)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>

            <CardFooter>
              <Button className="w-full" variant="outline" onClick={handleContactSales}>
                {t('landing.pricing.contactSales')}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>

      <PlanCheckoutModal open={modalOpen} onClose={handleModalClose} plan={selectedPlan} isYearly={isYearly} />
    </section>
  );
}
